import { Box, Button, Container, Typography } from "@mui/material";
import { ConfirmDialog, HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { ChevronRightIcon, LockIcon, UsersIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { AnalysisDetail } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useState } from "react";
import { TakeBackConfirmDialog } from "./Sharing/TakeBackConfirmDialog";
import { useReturnEditAccess } from "./Sharing/useReturnEditAccess";
import { getAgentFullName } from "./Sharing/utils";
import { type AnalysisAccess, AnalysisAccessStatus } from "./useAnalysisAccess";

const CTA_HEIGHT = 36;

type Props = {
  readonly analysis: AnalysisDetail;
  readonly access: AnalysisAccess;
  readonly isCreatingNewVersion: boolean;
  readonly onCreateNewVersion: () => void;
};

type BarContent = {
  readonly title: string;
  readonly text: string;
  readonly action: ReactNode;
};

type ActionLinkProps = {
  readonly loading?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
};

const ActionLink = ({ loading, onClick, children }: ActionLinkProps) => (
  <Button
    variant="link"
    loading={loading}
    onClick={onClick}
    // the negative margin cancels MUI's endIcon gap, the chevron's own side bearing is gap enough;
    // the spinner that replaces it while loading has none, so it keeps the gap and ends at the button's edge
    endIcon={<ChevronRightIcon fontSize="small" sx={{ ml: loading ? 0 : -Spacing.M }} />}
    sx={(theme) => ({
      height: CTA_HEIGHT,
      flexShrink: 0,
      "& .MuiButton-loadingIndicator": { right: 0 },
      "& .MuiSvgIcon-root": {
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      },
      "&:hover .MuiSvgIcon-root, &.Mui-focusVisible .MuiSvgIcon-root": { transform: "translateX(3px)" },
    })}
  >
    {children}
  </Button>
);

export const AnalysisStatusBar = ({ analysis, access, isCreatingNewVersion, onCreateNewVersion }: Props) => {
  const { t } = useResource();
  const { returnAccess, isReturning } = useReturnEditAccess();
  const [isTakeBackConfirmOpen, setIsTakeBackConfirmOpen] = useState(false);
  const [isReturnConfirmOpen, setIsReturnConfirmOpen] = useState(false);

  const createNewVersionButton = access.canCreateNewVersion ? (
    <ActionLink loading={isCreatingNewVersion} onClick={onCreateNewVersion}>
      {t(Resources.AnalysisDetail.StatusBar.CreateNewVersion)}
    </ActionLink>
  ) : null;

  const getContent = (): BarContent | null => {
    switch (access.status) {
      case AnalysisAccessStatus.sharedWithOther:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.SharedWithOther.Title, {
            advisorName: analysis.editingAgent ? getAgentFullName(analysis.editingAgent) : "",
            advisorId: analysis.editingAgent?.ssoId ?? "",
          }),
          text: t(Resources.AnalysisDetail.StatusBar.SharedWithOther.Text),
          action: access.isOwner ? (
            <ActionLink onClick={() => setIsTakeBackConfirmOpen(true)}>{t(Resources.AnalysisDetail.Sharing.TakeOver)}</ActionLink>
          ) : null,
        };
      case AnalysisAccessStatus.sharedWithMe:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.SharedWithMe.Title, { ownerName: analysis.agentName }),
          text: access.isEditable
            ? t(Resources.AnalysisDetail.StatusBar.SharedWithMe.Text)
            : t(Resources.AnalysisDetail.StatusBar.SharedWithMe.ReadOnlyText),
          action: (
            <>
              <ActionLink onClick={() => setIsReturnConfirmOpen(true)}>{t(Resources.AnalysisDetail.StatusBar.ReturnToAdvisor)}</ActionLink>
              {createNewVersionButton}
            </>
          ),
        };
      case AnalysisAccessStatus.declined:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.Declined.Title),
          text: t(Resources.AnalysisDetail.StatusBar.ReadOnlyText),
          action: createNewVersionButton,
        };
      case AnalysisAccessStatus.completed:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.Completed.Title),
          text: t(Resources.AnalysisDetail.StatusBar.ReadOnlyText),
          action: createNewVersionButton,
        };
      default:
        return null;
    }
  };

  const content = getContent();

  if (!content) {
    return null;
  }

  const isSharedWithMe = access.status === AnalysisAccessStatus.sharedWithMe;

  return (
    <>
      <Box
        component="output"
        display="block"
        sx={{
          bgcolor: isSharedWithMe ? colors.brandLight : colors.textTertiary,
          borderBottom: `1px solid ${isSharedWithMe ? colors.brandLightBorder : colors.border}`,
        }}
      >
        <Container>
          <HStack minHeight={52} alignItems="center" spacing={Spacing.L}>
            {isSharedWithMe ? (
              <UsersIcon sx={{ fontSize: 18, color: "primary.main" }} />
            ) : (
              <LockIcon sx={{ fontSize: 18, color: colors.darkGrey }} />
            )}
            <Typography variant="body2">
              <Box component="span" fontWeight={700}>
                {content.title}
              </Box>
              {/* the follow-up sentence only tells the user what the action does, so it goes away with the action */}
              {content.action && ` ${content.text}`}
            </Typography>
            {content.action}
          </HStack>
        </Container>
      </Box>
      <TakeBackConfirmDialog
        isOpen={isTakeBackConfirmOpen}
        advisor={access.sharedWithAgent}
        onClose={() => setIsTakeBackConfirmOpen(false)}
      />
      <ConfirmDialog
        open={isReturnConfirmOpen}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Return.Title)}
        message={t(Resources.AnalysisDetail.Sharing.Confirm.Return.Text, { ownerName: analysis.agentName })}
        cancelLabel={t(Resources.Common.Cancel)}
        confirmLabel={t(Resources.AnalysisDetail.StatusBar.ReturnToAdvisor)}
        onConfirm={() => returnAccess({ onSettled: () => setIsReturnConfirmOpen(false) })}
        onCancel={() => setIsReturnConfirmOpen(false)}
        isPending={isReturning}
      />
    </>
  );
};
